/* eslint-disable no-mixed-operators */
import { Error as AppError, ErrorDebugContext } from "@/util-components/root/context";
import { CheckCircle2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { getCurrentTraceId } from "../monitoring/sentry";
import { getBrowserEnvironment } from "../utils/environment";
import { trackErrorReportSent } from "@/lib/monitoring/error-interactions";

export const useErrorReport = () => {
  const sendReport = (error: AppError | Error, options?: { traceId?: string; correlationId?: string }) => {
    // Normalizing the error object
    let title = "Ein Fehler ist aufgetreten";
    let message = "Ein unerwarteter Fehler ist aufgetreten.";
    let statusCode = 0;
    let entity = "UNKNOWN_ERROR";
    let debugContext: ErrorDebugContext | undefined = undefined;
    let traceId = options?.traceId;
    let correlationId = options?.correlationId;

    if ('statusCode' in error) {
      // It's an AppError
      const appError = error as AppError;
      title = appError.title || title;
      message = appError.message || message;
      statusCode = appError.statusCode || 0;
      entity = appError.entity || "APP_ERROR";
      debugContext = appError.debugContext;
      traceId = traceId ?? appError.traceId;
      correlationId = correlationId ?? appError.correlationId;
    } else {
      // It's a standard JS Error (e.g. from ErrorBoundary)
      message = error.message;
      entity = error.name;
      traceId = traceId ?? getCurrentTraceId();
      // Construct a basic debug context for standard errors
      debugContext = {
        request: {
          method: "CLIENT_SIDE",
          url: window.location.href,
          headers: {},
        },
        response: {
          status: 0,
          statusText: "Client Side Exception",
          body: null
        },
        environment: getBrowserEnvironment(),
        stackTrace: error.stack,
        timestamp: new Date().toISOString(),
      };
    }

    // Console logging for debugging
    console.group("🚨 Fehlerbericht senden");
    console.log("Title:", title);
    console.log("Message:", message);
    console.log("Status Code:", statusCode);
    console.log("Entity:", entity);
    console.log("Trace ID:", traceId);
    console.log("Correlation ID:", correlationId);
    if (debugContext) {
      console.log("Debug Context:", debugContext);
    } else {
      console.warn("No debug context available.");
    }
    console.groupEnd();

    trackErrorReportSent({
      title,
      message,
      entity,
      statusCode,
      traceId,
      correlationId,
      debugContext,
    });

    // Construct email content
    const recipient = "support@sportypost.io";
    const subject = `Fehlerbericht: ${title}`;
    
    // Parse stack trace to find caller
    let callerInfo = "Unbekannt";
    let formattedStackTrace = "";
    
    if (debugContext?.stackTrace) {
      const lines = debugContext.stackTrace.split('\n');
      
      // Filter logic to find the relevant application code
      const callerLine = lines.find(line => {
        const trimmed = line.trim();
        if (!trimmed) return false;
        
        // Skip error message itself
        if (trimmed.startsWith('Error:')) return false;
        
        // Skip infrastructure and library code
        const isInfrastructure = 
          trimmed.includes('client.ts') || 
          trimmed.includes('node_modules') ||
          trimmed.includes('react-dom') ||
          trimmed.includes('react-router') ||
          trimmed.includes('regenerator-runtime') ||
          trimmed.includes('(native)') ||
          trimmed.includes('http://localhost') && trimmed.includes('bundle.js'); // Common dev bundle pattern if not mapped

        return !isInfrastructure;
      });

      if (callerLine) {
        // Clean up the line for display
        callerInfo = callerLine.trim()
          .replace(/^at /, '') // Remove Chrome 'at ' prefix
          .split('(')[0] // Try to get just the function name if possible
          .trim();
          
        // If function name is empty or anonymous, use the full line but truncated
        if (!callerInfo || callerInfo === 'async') {
           callerInfo = callerLine.trim().replace(/^at /, '');
        }
      }
      formattedStackTrace = debugContext.stackTrace;
    }

    let body = `Hallo SportyPost Support,

ich habe einen Fehler gefunden.

Details:
- Titel: ${title}
- Nachricht: ${message}
- Status Code: ${statusCode}
- Fehler-Code: ${entity}
- Trace-ID: ${traceId || "Nicht verfügbar"}
- Correlation-ID: ${correlationId || "Nicht verfügbar"}
- Ausgelöst in: ${callerInfo}

`;

    if (debugContext) {
      const { environment, request, response, timestamp } = debugContext;
      
      if (environment) {
        body += `Umgebung:
- Browser: ${environment.browser} ${environment.browserVersion}
- OS: ${environment.os} (${environment.platform})
- Screen: ${environment.screenSize}
- Window: ${environment.windowSize}
- User Agent: ${environment.userAgent}

`;
      }

      body += `Request Info:
- Method: ${request.method}
- URL: ${request.url}
- Timestamp: ${timestamp}

Response Info:
- Status: ${response.status} ${response.statusText}

Stack Trace:
${formattedStackTrace}

Debug Context (JSON):
${JSON.stringify(debugContext, null, 2)}
`;
    }

    // Open mail client
    window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    toast.success("Fehlerbericht wird vorbereitet", {
      description: "Dein Email-Programm sollte sich gleich öffnen.",
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      duration: 4000,
    });
  };

  return { sendReport };
};
